
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { usePuzzles } from '../PuzzleContext';
import { useAdmin } from '../AdminContext';
import { useAppContext } from '../AppContext';
import { UserProfile, defaultImageEdits, AIReport, ReportedPuzzle, ReportedUser, AIAutomationSettings } from '../types';
import BackButton from '../components/BackButton';
import ImageInput from '../components/ImageInput';
import ConfirmationModal from '../components/ConfirmationModal';
import { isUserOnline } from '../utils';
import { useAuth } from '../AuthContext';
import { functions, db } from '../firebase'; // Import DB for automation settings
import { DiagnosticLog, getLogs, clearLogs, logDiagnostic } from '../utils/logger';

type AdminView = 'dashboard' | 'users' | 'reports';

const Section: React.FC<{ title: string; children: React.ReactNode; icon: string }> = ({ title, children, icon }) => (
    <section className="bg-black/20 p-4 rounded-xl border border-white/10">
        <h2 className="font-bold text-xl text-white border-b border-white/10 pb-3 mb-4 flex items-center gap-3">
            <i className={`ph-duotone ph-${icon} text-2xl aurora-text`}></i>
            <span>{title}</span>
        </h2>
        <div className="space-y-4">{children}</div>
    </section>
);

const StatCard: React.FC<{ title: string; value: number | string; icon: string; iconBgColor: string; onClick?: () => void }> = ({ title, value, icon, iconBgColor, onClick }) => (
    <div 
        onClick={onClick}
        className={`bg-black/30 p-4 rounded-xl flex items-center space-x-4 border border-white/10 ${onClick ? 'cursor-pointer hover:bg-black/40 transition-transform hover:scale-105 active:scale-95' : ''}`}
    >
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${iconBgColor}`}>
            <i className={`ph-duotone ph-${icon} text-3xl text-white`}></i>
        </div>
        <div>
            <p className="text-gray-400 font-semibold">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {onClick && <p className="text-xs text-[var(--accent-pink)] font-bold mt-1">Manage <i className="ph-bold ph-arrow-right"></i></p>}
        </div>
    </div>
);

const Accordion: React.FC<{ isOpen: boolean; onToggle: () => void; title: string; count: number; children: React.ReactNode }> = ({ isOpen, onToggle, title, count, children }) => (
    <div className="bg-black/30 border border-white/10 rounded-lg">
        <button onClick={onToggle} className="w-full flex justify-between items-center p-4">
            <div className="flex items-center gap-2"><h3 className="font-semibold text-lg">{title}</h3>{count > 0 && <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">{count}</span>}</div>
            <i className={`ph-duotone ph-caret-down text-xl transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
        </button>
        {isOpen && <div className="p-4 border-t border-white/10 space-y-2">{children}</div>}
    </div>
);

const ManageUserModal: React.FC<{ 
    user: UserProfile | null, 
    onClose: () => void,
    userProfile: UserProfile | null,
    giveCluesToUser: any,
    giveAICreditsToUser: any,
    unbanUser: any,
    banUser: any,
    toggleBusinessStatus: any,
    banAndWipeUser: any,
    setConfirmProps: any,
    setConfirmOpen: any,
    addNotification: any
}> = ({ 
    user, onClose, userProfile, 
    giveCluesToUser, giveAICreditsToUser, unbanUser, banUser, 
    toggleBusinessStatus, banAndWipeUser, setConfirmProps, setConfirmOpen, addNotification 
}) => {
    const [clues, setClues] = useState<number | ''>('');
    const [credits, setCredits] = useState<number | ''>('');
    const [message, setMessage] = useState('');
    const [isGiving, setIsGiving] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    if (!user) return null;

    const handleGive = async (e: React.FormEvent) => {
        e.preventDefault();
        const numClues = typeof clues === 'number' ? clues : 0;
        const numCredits = typeof credits === 'number' ? credits : 0;
        if (numClues <= 0 && numCredits <= 0) return;
        setIsGiving(true);
        try {
            if (numClues > 0) await giveCluesToUser(user.uid, numClues);
            if (numCredits > 0) await giveAICreditsToUser(user.uid, numCredits);
            if (message.trim()) {
                await addNotification(user.uid, {
                    type: 'system',
                    text: message.trim(),
                });
            }
            setClues(''); setCredits(''); setMessage('');
            alert(`Successfully gave items to ${user.displayName}!`);
        } finally { setIsGiving(false); }
    };

    const handleBanToggle = async () => {
        setIsUpdatingStatus(true);
        try {
            if (user.isBanned) await unbanUser(user.uid);
            else await banUser(user.uid);
            onClose();
        } finally { setIsUpdatingStatus(false); }
    };

    const handleBusinessToggle = async () => {
        setIsUpdatingStatus(true);
        try {
            await toggleBusinessStatus(user.uid, !user.isBusiness);
            onClose();
        } finally { setIsUpdatingStatus(false); }
    };

    const handleBanAndWipeToggle = async () => {
        setConfirmProps({
            title: 'Ban & Wipe Permanent?',
            message: `Are you sure you want to permanently BAN and DELETE ${user.displayName}? This will also delete their posts. This action is IRREVERSIBLE.`,
            onConfirm: async () => {
                setIsUpdatingStatus(true);
                try {
                    await banAndWipeUser(user);
                    setConfirmOpen(false);
                    onClose();
                } finally {
                    setIsUpdatingStatus(false);
                }
            },
            confirmText: 'Ban & Wipe permanent'
        });
        setConfirmOpen(true);
    }
    
    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="bg-[var(--primary-dark-purple)] p-6 rounded-2xl shadow-2xl w-full max-w-sm flex flex-col border border-white/20 animate-modal-pop-in" 
                onClick={e => e.stopPropagation()}
            >
                <div className="flex flex-col items-center pb-6 border-b border-white/10">
                    <div className="relative">
                        <img src={user.photoURL} className="w-20 h-20 rounded-full border-4 border-white/20 object-cover shadow-lg" alt={user.displayName} />
                        {isUserOnline(user) && (
                            <span className="absolute bottom-1 right-1 block h-4 w-4 rounded-full bg-green-500 border-2 border-[var(--primary-dark-purple)]"></span>
                        )}
                    </div>
                    <h3 className="font-bold text-xl mt-3 text-white">{user.displayName} {user.uid === userProfile?.uid && <span className="text-sm text-gray-300">(You)</span>}</h3>
                    {user.email ? (
                        <p className="text-xs text-emerald-400 font-mono mt-1 opacity-80">{user.email}</p>
                    ) : (
                        <p className="text-xs text-orange-400 italic mt-1 opacity-80">Email not tracked (Legacy User)</p>
                    )}
                </div>
                <form onSubmit={handleGive} className="py-6 space-y-3">
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Give Clues</label>
                            <input type="number" min="0" value={clues} onChange={e => setClues(parseInt(e.target.value) || '')} className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white text-center font-bold" />
                        </div>
                        <div className="flex-1">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Give AI Credits</label>
                            <input type="number" min="0" value={credits} onChange={e => setCredits(parseInt(e.target.value) || '')} className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white text-center font-bold" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Message (Optional)</label>
                        <input type="text" value={message} onChange={e => setMessage(e.target.value)} placeholder="Send a message..." className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white" />
                    </div>
                    <button type="submit" disabled={isGiving || ((typeof clues !== 'number' || clues <= 0) && (typeof credits !== 'number' || credits <= 0))} className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold py-2 rounded-lg shadow-lg disabled:opacity-50">
                        {isGiving ? 'Giving...' : 'Give Items'}
                    </button>
                </form>
                <div className="pt-2 flex flex-col gap-3">
                    <button onClick={handleBusinessToggle} disabled={isUpdatingStatus} className={`w-full font-bold py-3 rounded-lg shadow-md transition-all ${user.isBusiness ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                        {isUpdatingStatus ? 'Processing...' : user.isBusiness ? 'Remove Business Status' : 'Upgrade to Business'}
                    </button>
                    <button onClick={handleBanToggle} disabled={isUpdatingStatus} className={`w-full font-bold py-3 rounded-lg shadow-md transition-all ${user.isBanned ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-red-600 hover:bg-red-500 text-white'}`}>
                        {isUpdatingStatus ? 'Processing...' : user.isBanned ? 'Unban User' : 'Ban User'}
                    </button>
                    <button onClick={handleBanAndWipeToggle} disabled={isUpdatingStatus} className="w-full bg-black text-white font-bold py-3 rounded-lg shadow-md hover:bg-gray-900 transition-all flex items-center justify-center gap-2 border border-white/20">
                        <i className="ph-bold ph-trash"></i>
                        {isUpdatingStatus ? 'Processing...' : 'Ban & Wipe Permanent'}
                    </button>
                    <button onClick={onClose} className="w-full bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg transition-colors">Close</button>
                </div>
            </div>
        </div>
    );
};

const AdminPage: React.FC = () => {
    // --- State and Hooks ---
    const { reportedPuzzles, reinstatePuzzle, deleteReportedPuzzle } = usePuzzles();
    const { puzzles: allPuzzles } = usePuzzles();
    const { 
        reportedUsers, aiReports,
        banUser, unbanUser, deleteUser, banAndWipeUser, resetLeaderboard, sendBroadcastMessage, 
        giveCluesToUser, giveAICreditsToUser, toggleBusinessStatus, addNotification,
        compensateUser
    } = useAdmin();
    const { appConfig, updateAppConfig } = useAppContext();
    const { allUsers, userProfile } = useAuth();
    
    // View State
    const [currentView, setCurrentView] = useState<AdminView>('dashboard');

    // State for User Management
    const [userSearch, setUserSearch] = useState('');
    const [managedUser, setManagedUser] = useState<UserProfile | null>(null);
    
    // State for AI Generation
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [aiGenStatus, setAiGenStatus] = useState('');
    
    // State for AI Automation Settings
    const [automationSettings, setAutomationSettings] = useState<AIAutomationSettings>({
        dailyChallengeEnabled: false,
        singlePostEnabled: false,
        singlePostIntervalHours: 4
    });
    
    // State for Confirmation Modals
    const [isConfirmOpen, setConfirmOpen] = useState(false);
    const [confirmProps, setConfirmProps] = useState({ title: '', message: '', onConfirm: () => {}, confirmText: 'Confirm' });
    
    // State for Accordion
    const [openAccordion, setOpenAccordion] = useState<string | null>('ai');

    // State for Compensation
    const [compSearchTerm, setCompSearchTerm] = useState('');
    const [compSelectedUser, setCompSelectedUser] = useState<UserProfile | null>(null);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [compPoints, setCompPoints] = useState(0);
    const [compAiCredits, setCompAiCredits] = useState(0);
    const [compClues, setCompClues] = useState(0);
    const [compMessage, setCompMessage] = useState('');
    const [compLoading, setCompLoading] = useState(false);
    const [compSuccess, setCompSuccess] = useState('');
    const [compError, setCompError] = useState('');

    // --- Diagnostic Logs States ---
    const [diagnosticLogs, setDiagnosticLogs] = useState<DiagnosticLog[]>(getLogs());
    const [diagFilterLevel, setDiagFilterLevel] = useState<string>('all');
    const [diagSearchQuery, setDiagSearchQuery] = useState<string>('');

    // Sync Diagnostic Logs live inside the Admin Page
    useEffect(() => {
        const handleLogsUpdate = () => {
            setDiagnosticLogs(getLogs());
        };
        window.addEventListener('app-log-updated', handleLogsUpdate);
        return () => window.removeEventListener('app-log-updated', handleLogsUpdate);
    }, []);

    const filteredCompUsers = useMemo(() => {
        if (!compSearchTerm) return [];
        const searchLower = compSearchTerm.toLowerCase();
        return allUsers.filter(u => u.displayName.toLowerCase().includes(searchLower)).slice(0, 10);
    }, [compSearchTerm, allUsers]);

    // Fetch Automation Settings
    useEffect(() => {
        const unsubscribe = db.collection('settings').doc('aiAutomation').onSnapshot((doc: any) => {
            if (doc.exists) {
                setAutomationSettings(doc.data() as AIAutomationSettings);
            }
        });
        return () => unsubscribe();
    }, []);

    const updateAutomationSettings = async (updates: Partial<AIAutomationSettings>) => {
        try {
            await db.collection('settings').doc('aiAutomation').set({
                ...automationSettings,
                ...updates
            }, { merge: true });
        } catch (error) {
            console.error("Failed to update automation settings", error);
            alert("Failed to save settings. Ensure you are an admin.");
        }
    };

    const formatTimestamp = (ts: any) => {
        if (!ts) return 'Never';
        return ts.toDate().toLocaleString();
    };

    const filteredUsers = useMemo(() => {
        let users = allUsers;
        if (userSearch) {
            users = allUsers.filter(u =>
                u.displayName.toLowerCase().includes(userSearch.toLowerCase())
            );
        }
        return [...users].sort((a, b) => {
            if (a.uid === userProfile?.uid) return -1;
            if (b.uid === userProfile?.uid) return 1;
            return a.displayName.localeCompare(b.displayName);
        });
    }, [allUsers, userSearch, userProfile]);

    const pendingAIReportsCount = aiReports.filter(r => r.status !== 'resolved').length;
    const pendingReportsCount = reportedPuzzles.length + reportedUsers.length + pendingAIReportsCount;

    const handleCompensateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!compSelectedUser) {
            setCompError('Please select a user from the dropdown.');
            return;
        }
        setCompLoading(true);
        setCompSuccess('');
        setCompError('');
        try {
            await compensateUser(compSelectedUser.displayName, compPoints, compAiCredits, compClues, compMessage);
            setCompSuccess(`Successfully compensated ${compSelectedUser.displayName}!`);
            setCompSelectedUser(null);
            setCompSearchTerm('');
            setCompPoints(0);
            setCompAiCredits(0);
            setCompClues(0);
            setCompMessage('');
        } catch (error: any) {
            setCompError(error.message || 'Failed to compensate user.');
        } finally {
            setCompLoading(false);
        }
    };

    // --- AI Generation Handlers ---
    const handleGenerateAIPost = async () => {
        setIsGeneratingAI(true);
        setAiGenStatus('Asking AI Game Master to create a puzzle...');
        try {
            const generateAIPost = functions.httpsCallable('generateAIPost');
            const result = await generateAIPost({ topic: "Random interesting object or place" });
            const data = result.data as any;
            setAiGenStatus(`Success! Created: ${data.message}`);
            alert(`New AI Puzzle Created!`);
        } catch (error: any) {
            console.error("AI Gen Error:", error);
            setAiGenStatus(`Error: ${error.message}`);
            alert("Failed to generate AI post. Check console.");
        } finally {
            setIsGeneratingAI(false);
        }
    };

    const handleGenerateDailyChallenge = async () => {
        setIsGeneratingAI(true);
        setAiGenStatus('Generating 3 Daily Challenges... this may take 60s...');
        try {
            const generateDailyChallenge = functions.httpsCallable('generateDailyChallenge');
            const result = await generateDailyChallenge({});
            const data = result.data as any;
            setAiGenStatus(`Success! ${data.message}`);
            alert("Daily Challenges Updated!");
        } catch (error: any) {
            console.error("AI Daily Error:", error);
            setAiGenStatus(`Error: ${error.message}`);
            alert("Failed to generate daily challenges.");
        } finally {
            setIsGeneratingAI(false);
        }
    };

    // --- Confirmation Handlers ---
    const handleResetClick = () => {
        setConfirmProps({ title: 'Reset Leaderboard?', message: 'This will set all user scores to 0. This cannot be undone.', onConfirm: async () => { await resetLeaderboard(); setConfirmOpen(false); }, confirmText: 'Reset Scores' });
        setConfirmOpen(true);
    };
    
    const handleBroadcastClick = () => {
        setConfirmProps({ title: 'Send Broadcast?', message: 'This will send the policy reminder to ALL users. Are you sure?', onConfirm: async () => { await sendBroadcastMessage(); setConfirmOpen(false); }, confirmText: 'Send to All' });
        setConfirmOpen(true);
    };

    const handleBanPost = (reportId: string, clueText: string) => {
        setConfirmProps({ 
            title: 'Ban Post?', 
            message: `Are you sure you want to permanently delete the post "${clueText}"? The user will be notified.`, 
            onConfirm: async () => { 
                await deleteReportedPuzzle(reportId); 
                setConfirmOpen(false); 
            }, 
            confirmText: 'Ban & Delete' 
        });
        setConfirmOpen(true);
    };

    const handleKeepPost = (reportId: string, clueText: string) => {
        setConfirmProps({ 
            title: 'Reinstate Post?', 
            message: `This will restore the post "${clueText}" to the feed and dismiss the report. The user will be notified.`, 
            onConfirm: async () => { 
                await reinstatePuzzle(reportId); 
                setConfirmOpen(false); 
            }, 
            confirmText: 'Reinstate' 
        });
        setConfirmOpen(true);
    };

    // --- Components ---
    
    // --- Sub Views ---

    const renderDashboard = () => (
        <div className="space-y-8 animate-fade-in">
            <Section title="Dashboard" icon="gauge">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <StatCard title="Total Users" value={allUsers.length} icon="users" iconBgColor="bg-blue-500/80" onClick={() => setCurrentView('users')} />
                    <StatCard title="Pending Reports" value={pendingReportsCount} icon="warning-circle" iconBgColor="bg-red-500/80" onClick={() => setCurrentView('reports')} />
                    <StatCard title="Total Puzzles" value={allPuzzles.length} icon="puzzle-piece" iconBgColor="bg-emerald-500/80" />
                </div>
            </Section>

            {/* AI SECTION */}
            <Section title="AI Content Generator" icon="brain">
                <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 p-4 rounded-lg border border-purple-500/30">
                    <p className="text-gray-300 mb-4 text-sm">Automate or manually generate content using the "AI Game Master" bot.</p>
                    
                    {/* Automation Controls */}
                    <div className="mb-6 p-4 bg-black/30 rounded-lg border border-white/10 space-y-4">
                        <h4 className="font-bold text-white text-sm uppercase tracking-wide border-b border-white/10 pb-2 mb-3">Automation Control</h4>
                        
                        {/* Daily Challenge Toggle */}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-bold text-white">Auto Daily Challenges</p>
                                <p className="text-xs text-gray-400">Generates 3 puzzles every 24h at midnight.</p>
                                {automationSettings.lastDailyRun && (
                                    <p className="text-[10px] text-emerald-400 mt-1">Last Run: {formatTimestamp(automationSettings.lastDailyRun)}</p>
                                )}
                            </div>
                            <button 
                                onClick={() => updateAutomationSettings({ dailyChallengeEnabled: !automationSettings.dailyChallengeEnabled })}
                                className={`w-12 h-6 rounded-full p-1 transition-colors ${automationSettings.dailyChallengeEnabled ? 'bg-green-500' : 'bg-gray-600'}`}
                            >
                                <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${automationSettings.dailyChallengeEnabled ? 'translate-x-6' : 'translate-x-0'}`}></div>
                            </button>
                        </div>

                        {/* Single Post Toggle */}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-bold text-white">Auto Single Posts</p>
                                <p className="text-xs text-gray-400">Posts to global feed automatically.</p>
                                {automationSettings.lastSinglePostRun && (
                                    <p className="text-[10px] text-emerald-400 mt-1">Last Run: {formatTimestamp(automationSettings.lastSinglePostRun)}</p>
                                )}
                            </div>
                            <button 
                                onClick={() => updateAutomationSettings({ singlePostEnabled: !automationSettings.singlePostEnabled })}
                                className={`w-12 h-6 rounded-full p-1 transition-colors ${automationSettings.singlePostEnabled ? 'bg-green-500' : 'bg-gray-600'}`}
                            >
                                <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${automationSettings.singlePostEnabled ? 'translate-x-6' : 'translate-x-0'}`}></div>
                            </button>
                        </div>

                        {/* Timer Input */}
                        {automationSettings.singlePostEnabled && (
                            <div className="flex items-center gap-3 bg-white/5 p-2 rounded-lg">
                                <label className="text-sm font-bold text-gray-300 whitespace-nowrap">Frequency (Hours):</label>
                                <input 
                                    type="number" 
                                    min="1" 
                                    max="24" 
                                    value={automationSettings.singlePostIntervalHours} 
                                    onChange={(e) => updateAutomationSettings({ singlePostIntervalHours: Math.max(1, parseInt(e.target.value)) })}
                                    className="w-full bg-black/30 border border-white/20 rounded px-2 py-1 text-white font-mono text-center"
                                />
                            </div>
                        )}
                    </div>

                    {/* Manual Controls */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button 
                            onClick={handleGenerateAIPost} 
                            disabled={isGeneratingAI}
                            className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 px-4 rounded-lg shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGeneratingAI ? <div className="spinner-white h-5 w-5"></div> : <i className="ph-duotone ph-magic-wand"></i>}
                            Generate Single Post
                        </button>
                        <button 
                            onClick={handleGenerateDailyChallenge} 
                            disabled={isGeneratingAI}
                            className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-4 rounded-lg shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGeneratingAI ? <div className="spinner-white h-5 w-5"></div> : <i className="ph-duotone ph-trophy"></i>}
                            Generate Daily Challenges
                        </button>
                    </div>
                    {aiGenStatus && (
                        <div className="mt-3 text-xs font-mono bg-black/40 p-2 rounded text-green-400">
                            {aiGenStatus}
                        </div>
                    )}
                </div>
            </Section>

            <Section title="Compensate User" icon="gift">
                <form onSubmit={handleCompensateUser} className="bg-black/30 p-4 rounded-lg border border-white/10 space-y-4">
                    <div className="relative">
                        <label className="block text-sm font-bold text-gray-300 mb-1">Select User</label>
                        {compSelectedUser ? (
                            <div className="flex items-center justify-between bg-white/10 border border-white/20 rounded-lg p-2">
                                <div className="flex items-center gap-2">
                                    <img src={compSelectedUser.photoURL} className="w-8 h-8 rounded-full object-cover" alt="" />
                                    <span className="text-white font-bold">{compSelectedUser.displayName}</span>
                                </div>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setCompSelectedUser(null);
                                        setCompSearchTerm('');
                                    }}
                                    className="text-gray-400 hover:text-white"
                                >
                                    <i className="ph-bold ph-x"></i>
                                </button>
                            </div>
                        ) : (
                            <>
                                <input 
                                    type="text" 
                                    value={compSearchTerm} 
                                    onChange={e => {
                                        setCompSearchTerm(e.target.value);
                                        setShowUserDropdown(true);
                                    }} 
                                    onFocus={() => setShowUserDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white" 
                                    placeholder="Search by username..." 
                                />
                                {showUserDropdown && compSearchTerm && (
                                    <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto custom-scrollbar">
                                        {filteredCompUsers.length > 0 ? (
                                            filteredCompUsers.map(u => (
                                                <div 
                                                    key={u.uid} 
                                                    className="p-2 hover:bg-gray-700 cursor-pointer text-white flex items-center gap-2"
                                                    onClick={() => {
                                                        setCompSelectedUser(u);
                                                        setCompSearchTerm('');
                                                        setShowUserDropdown(false);
                                                    }}
                                                >
                                                    <img src={u.photoURL} className="w-6 h-6 rounded-full object-cover" alt="" />
                                                    <span>{u.displayName}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-2 text-gray-400 text-sm">No users found</div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm font-bold text-gray-300 mb-1">Points</label>
                            <input type="number" min="0" value={compPoints} onChange={e => setCompPoints(parseInt(e.target.value) || 0)} className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-300 mb-1">AI Credits</label>
                            <input type="number" min="0" value={compAiCredits} onChange={e => setCompAiCredits(parseInt(e.target.value) || 0)} className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-300 mb-1">Clues</label>
                            <input type="number" min="0" value={compClues} onChange={e => setCompClues(parseInt(e.target.value) || 0)} className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-300 mb-1">Notification Message (Optional)</label>
                        <textarea value={compMessage} onChange={e => setCompMessage(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white" placeholder="Message to send to the user..." rows={2}></textarea>
                    </div>
                    {compError && <p className="text-red-400 text-sm font-bold">{compError}</p>}
                    {compSuccess && <p className="text-green-400 text-sm font-bold">{compSuccess}</p>}
                    <button type="submit" disabled={compLoading || (!compPoints && !compAiCredits && !compClues && !compMessage)} className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold py-2 rounded-lg shadow-lg disabled:opacity-50">
                        {compLoading ? 'Processing...' : 'Compensate User'}
                    </button>
                </form>
            </Section>

            <Section title="App Settings" icon="gear">
                <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={handleResetClick} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg">Reset Leaderboard</button>
                    <button onClick={handleBroadcastClick} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-lg">Send Broadcast</button>
                </div>
            </Section>

            {/* Purchase & System Diagnostics Console Embedded Section */}
            <Section title="Purchase & System Diagnostics" icon="terminal">
                <div className="bg-black/40 p-4 rounded-xl border border-purple-500/30 space-y-4">
                    <p className="text-gray-300 text-sm">
                        View active system transactions, network logging, and app store platform integration failures in real time.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 items-center justify-between pb-3 border-b border-white/10">
                        {/* Filters and Search */}
                        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="Filter by message/module..."
                                value={diagSearchQuery}
                                onChange={(e) => setDiagSearchQuery(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50 flex-1 min-w-[150px]"
                            />
                            <select
                                value={diagFilterLevel}
                                onChange={(e) => setDiagFilterLevel(e.target.value)}
                                className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-purple-500/50"
                            >
                                <option value="all">All Levels</option>
                                <option value="info">Info</option>
                                <option value="success">Success</option>
                                <option value="warn">Warn</option>
                                <option value="error">Error</option>
                            </select>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 w-full sm:w-auto justify-end">
                            <button
                                onClick={() => {
                                    logDiagnostic('info', 'TEST', 'Diagnostic test log initiated by Admin.');
                                }}
                                className="bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/40 text-purple-200 px-3 py-1.5 text-xs font-bold transition-all rounded"
                            >
                                Test Log
                            </button>
                            <button
                                onClick={() => {
                                    clearLogs();
                                    alert("Diagnostic logs cleared.");
                                }}
                                className="bg-red-900/30 hover:bg-red-950 text-red-200 border border-red-500/30 px-3 py-1.5 text-xs font-bold transition-all rounded"
                            >
                                Clear Console
                            </button>
                        </div>
                    </div>

                    {/* Terminal Window */}
                    <div className="bg-zinc-950 rounded-lg border border-white/5 p-3 h-72 overflow-y-auto font-mono text-xs text-green-400 space-y-2.5 custom-scrollbar">
                        {diagnosticLogs.filter(log => {
                            if (diagFilterLevel !== 'all' && log.level !== diagFilterLevel) return false;
                            if (diagSearchQuery) {
                                const q = diagSearchQuery.toLowerCase();
                                return (
                                    log.message.toLowerCase().includes(q) ||
                                    log.module.toLowerCase().includes(q) ||
                                    (log.details && log.details.toLowerCase().includes(q))
                                );
                            }
                            return true;
                        }).length > 0 ? (
                            diagnosticLogs.filter(log => {
                                if (diagFilterLevel !== 'all' && log.level !== diagFilterLevel) return false;
                                if (diagSearchQuery) {
                                    const q = diagSearchQuery.toLowerCase();
                                    return (
                                        log.message.toLowerCase().includes(q) ||
                                        log.module.toLowerCase().includes(q) ||
                                        (log.details && log.details.toLowerCase().includes(q))
                                    );
                                }
                                return true;
                            }).map((log, index) => {
                                let badgeColor = 'text-blue-400 bg-blue-500/10 border-blue-500/20';
                                if (log.level === 'error') badgeColor = 'text-red-400 bg-red-500/10 border-red-500/20';
                                if (log.level === 'warn') badgeColor = 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
                                if (log.level === 'success') badgeColor = 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';

                                return (
                                    <div key={index} className="border-b border-white/5 pb-2 last:border-0">
                                        <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                                            <span className="font-semibold text-purple-400">[{log.timestamp}] {log.module.toUpperCase()}</span>
                                            <span className={`px-1.5 py-0.5 rounded border ${badgeColor} uppercase text-[9px] font-bold`}>
                                                {log.level}
                                            </span>
                                        </div>
                                        <p className={`font-semibold ${log.level === 'error' ? 'text-red-300' : log.level === 'warn' ? 'text-yellow-200' : log.level === 'success' ? 'text-emerald-300' : 'text-gray-200'}`}>
                                            {log.message}
                                        </p>
                                        {log.details && (
                                            <pre className="mt-1.5 p-2 bg-black/40 border border-white/5 rounded text-[10px] text-gray-400 whitespace-pre-wrap overflow-x-auto max-h-36">
                                                {log.details}
                                            </pre>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="h-full flex items-center justify-center text-gray-600 italic">
                                No system diagnostic logs match search filters.
                            </div>
                        )}
                    </div>
                </div>
            </Section>
        </div>
    );

    const renderUsersView = () => (
        <div className="space-y-4 animate-fade-in">
            <button onClick={() => setCurrentView('dashboard')} className="text-white font-bold flex items-center hover:opacity-80">
                <i className="ph-duotone ph-arrow-left mr-2"></i> Back to Dashboard
            </button>
            <Section title="Manage Users" icon="users">
                <div className="relative mb-4">
                    <i className="ph-duotone ph-magnifying-glass absolute top-1/2 left-3 -translate-y-1/2 text-gray-400"></i>
                    <input type="text" placeholder="Search for user..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="w-full p-2 pl-10 border-white/20 rounded bg-black/30 placeholder-gray-400 text-white" />
                </div>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2 -mr-2">
                    {filteredUsers.length > 0 ? filteredUsers.map(user => {
                        const isNew = user.createdAt && (Date.now() - user.createdAt.toMillis()) < 3 * 24 * 60 * 60 * 1000;
                        return (
                        <div key={user.uid} className={`p-2 rounded-lg flex items-center justify-between ${user.uid === userProfile?.uid ? 'bg-purple-900/50 border border-purple-500/50' : 'bg-black/30'}`}>
                            <div className="flex items-center space-x-3 min-w-0">
                                <img src={user.photoURL} className="w-10 h-10 rounded-full object-cover" alt={user.displayName} />
                                <div className="min-w-0">
                                    <p className="font-bold truncate">
                                        {user.displayName}
                                        {isNew && <span className="ml-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full align-middle">NEW</span>}
                                    </p>
                                    <p className={`text-xs ${user.isBanned ? 'text-red-400' : 'text-gray-400'}`}>{user.isBanned ? 'BANNED' : `Score: ${user.score}`}</p>
                                </div>
                            </div>
                            <button onClick={() => setManagedUser(user)} className="bg-white/10 font-bold text-xs px-3 py-1 rounded-md hover:bg-white/20">Manage</button>
                        </div>
                    )}) : <p className="text-center text-gray-400 py-8">No users found.</p>}
                </div>
            </Section>
        </div>
    );

    const renderReportsView = () => (
        <div className="space-y-4 animate-fade-in">
            <button onClick={() => setCurrentView('dashboard')} className="text-white font-bold flex items-center hover:opacity-80">
                <i className="ph-duotone ph-arrow-left mr-2"></i> Back to Dashboard
            </button>
            <Section title="Reports Center" icon="warning">
                <div className="space-y-4">
                    <Accordion isOpen={openAccordion === 'ai'} onToggle={() => setOpenAccordion(openAccordion === 'ai' ? null : 'ai')} title="AI Triage" count={pendingAIReportsCount}>{aiReports.length > 0 ? aiReports.map(r => <div key={r.reportId} className={`p-2 rounded text-sm ${r.status==='resolved' ? 'bg-white/5' : 'bg-white/10'}`}>AI Report from {r.reporterDisplayName}: "{r.summary}"</div>) : <p className="text-gray-400 text-center">No AI reports.</p>}</Accordion>
                    <Accordion isOpen={openAccordion === 'posts'} onToggle={() => setOpenAccordion(openAccordion === 'posts' ? null : 'posts')} title="Reported Posts" count={reportedPuzzles.length}>
                        {reportedPuzzles.length > 0 ? reportedPuzzles.map(r => (
                            <div key={r.reportId} className="bg-white/10 rounded-lg p-3 space-y-3">
                                <div className="flex items-start gap-3">
                                    <img src={r.puzzle.imageUrl} alt="Reported content" className="w-20 h-20 rounded-md object-cover flex-shrink-0 bg-black/50" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm text-yellow-400 mb-1">Reason: {r.reason}</p>
                                        <p className="text-xs text-gray-300 italic mb-2">"{r.details || 'No details'}"</p>
                                        <p className="text-gray-400 text-xs">{r.puzzle.clue}</p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleKeepPost(r.reportId, r.puzzle.clue)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 rounded text-xs">Keep</button>
                                    <button onClick={() => handleBanPost(r.reportId, r.puzzle.clue)} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 rounded text-xs">Delete</button>
                                </div>
                            </div>
                        )) : <p className="text-gray-400 text-center">No reported posts.</p>}
                    </Accordion>
                    <Accordion isOpen={openAccordion === 'users'} onToggle={() => setOpenAccordion(openAccordion === 'users' ? null : 'users')} title="Reported Users" count={reportedUsers.length}>{reportedUsers.length > 0 ? reportedUsers.map(r => <div key={r.reportId} className="p-2 bg-white/10 rounded text-sm">User Report: "{r.user.displayName}"</div>) : <p className="text-gray-400 text-center">No reported users.</p>}</Accordion>
                </div>
            </Section>
        </div>
    );

    return (
        <>
            <div className="-m-4 bg-[var(--primary-dark-purple)] text-white p-4 space-y-8 min-h-screen">
                <div className="flex items-center gap-3">
                    <BackButton colorClass="text-white" text="Back to Profile" />
                </div>
                
                <h1 className="text-3xl font-bold text-center aurora-text">Admin Command Center</h1>

                {currentView === 'dashboard' && renderDashboard()}
                {currentView === 'users' && renderUsersView()}
                {currentView === 'reports' && renderReportsView()}
            </div>
            
            <ManageUserModal 
                user={managedUser} 
                onClose={() => setManagedUser(null)} 
                userProfile={userProfile}
                giveCluesToUser={giveCluesToUser}
                giveAICreditsToUser={giveAICreditsToUser}
                unbanUser={unbanUser}
                banUser={banUser}
                toggleBusinessStatus={toggleBusinessStatus}
                banAndWipeUser={banAndWipeUser}
                setConfirmProps={setConfirmProps}
                setConfirmOpen={setConfirmOpen}
                addNotification={addNotification}
            />
            <ConfirmationModal isOpen={isConfirmOpen} onClose={() => setConfirmOpen(false)} title={confirmProps.title} message={confirmProps.message} onConfirm={confirmProps.onConfirm} confirmText={confirmProps.confirmText} triggerRect={null}/>
        </>
    );
};

export default AdminPage;
